/** DLSG v1 snap-grid reader — nearest graph vertex for a map click.
 *
 * Written by factory/dustlark_factory/stages/snapgrid.py (see its module
 * docstring for the authoritative layout). Little-endian:
 *
 *   header (24 B): magic "DLSG", u32 version=1, f32 cellDeg, u32 nCells,
 *                  u32 nEntries, u32 reserved
 *   cells   nCells x 16B (u32 ix, u32 iy, u32 firstEntry, u32 count),
 *           sorted by (ix, iy) — binary-searchable
 *   entries nEntries x 12B (u32 vertexId, f32 lon, f32 lat), grouped by
 *           cell in the cells-section order
 *
 * ix = floor((lon+180)/cellDeg), iy = floor((lat+90)/cellDeg).
 *
 * The cell index is fetched eagerly at open (a few MB once per state);
 * each nearest() then range-reads only the 3x3 cells around the query. */

import type { RangeSource } from "./dlr2";
import { haversineM } from "./geo";
import type { LonLat } from "./types";

export interface SnapHit {
  id: number;
  at: LonLat;
  distM: number;
}

const HEADER_BYTES = 24;
const CELL_BYTES = 16;
const ENTRY_BYTES = 12;
const DEFAULT_TOLERANCE_M = 250;

/** A run of entries, in ENTRY units (not bytes): [start, start+count). */
interface EntryRange {
  start: number;
  count: number;
}

function readMagic(view: DataView): string {
  return String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
}

/** Merge overlapping/adjacent ranges (sorted by start) into fewer, larger
 * ones — turns up to 9 per-cell entry slices into as few ranged reads as
 * the cell layout allows. */
function mergeRanges(ranges: EntryRange[]): EntryRange[] {
  if (ranges.length === 0) return [];
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: EntryRange[] = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1];
    const cur = sorted[i];
    if (cur.start <= last.start + last.count) {
      last.count = Math.max(last.count, cur.start + cur.count - last.start);
    } else {
      merged.push({ ...cur });
    }
  }
  return merged;
}

export class SnapGrid {
  private readonly src: RangeSource;
  private readonly cellDeg: number;
  private readonly entriesBase: number;
  // Parallel arrays, sorted by (ix, iy) ascending — the cells table as
  // written by write_snapgrid, decoded once at open.
  private readonly cellIx: Uint32Array;
  private readonly cellIy: Uint32Array;
  private readonly cellFirst: Uint32Array;
  private readonly cellCount: Uint32Array;

  private constructor(
    src: RangeSource,
    cellDeg: number,
    entriesBase: number,
    cellIx: Uint32Array,
    cellIy: Uint32Array,
    cellFirst: Uint32Array,
    cellCount: Uint32Array,
  ) {
    this.src = src;
    this.cellDeg = cellDeg;
    this.entriesBase = entriesBase;
    this.cellIx = cellIx;
    this.cellIy = cellIy;
    this.cellFirst = cellFirst;
    this.cellCount = cellCount;
  }

  static async open(src: RangeSource): Promise<SnapGrid> {
    const headerBuf = await src.read(0, HEADER_BYTES);
    const header = new DataView(headerBuf);
    const magic = readMagic(header);
    if (magic !== "DLSG") {
      throw new Error(`SnapGrid.open: bad magic ${JSON.stringify(magic)} (expected "DLSG")`);
    }
    const version = header.getUint32(4, true);
    if (version !== 1) {
      throw new Error(`SnapGrid.open: unsupported DLSG version ${version} (expected 1)`);
    }
    const cellDeg = header.getFloat32(8, true);
    const nCells = header.getUint32(12, true);
    const nEntries = header.getUint32(16, true);

    const cellsBase = HEADER_BYTES;
    const entriesBase = cellsBase + nCells * CELL_BYTES;
    const totalBytes = entriesBase + nEntries * ENTRY_BYTES;

    const fileSize = await src.size();
    if (fileSize < totalBytes) {
      throw new Error(
        `SnapGrid.open: truncated source — sections need ${totalBytes} B, source has ${fileSize} B`,
      );
    }

    // The cell index is small (a few MB at Utah scale) — fetch it eagerly
    // so nearest() only ever needs to range-read the 3x3 entries slices.
    const cellsBuf = await src.read(cellsBase, nCells * CELL_BYTES);
    const cellsView = new DataView(cellsBuf);
    const cellIx = new Uint32Array(nCells);
    const cellIy = new Uint32Array(nCells);
    const cellFirst = new Uint32Array(nCells);
    const cellCount = new Uint32Array(nCells);
    for (let i = 0; i < nCells; i++) {
      const off = i * CELL_BYTES;
      cellIx[i] = cellsView.getUint32(off, true);
      cellIy[i] = cellsView.getUint32(off + 4, true);
      cellFirst[i] = cellsView.getUint32(off + 8, true);
      cellCount[i] = cellsView.getUint32(off + 12, true);
    }

    console.debug(`SnapGrid.open: ${nCells} cells, ${nEntries} entries, cellDeg=${cellDeg}`);

    return new SnapGrid(src, cellDeg, entriesBase, cellIx, cellIy, cellFirst, cellCount);
  }

  /** Binary search for the cell at (ix, iy); -1 when it doesn't exist —
   * the grid is sparse, most of the plane has no vertices in it. */
  private findCell(ix: number, iy: number): number {
    let lo = 0;
    let hi = this.cellIx.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >>> 1;
      const cmp = this.cellIx[mid] !== ix ? this.cellIx[mid] - ix : this.cellIy[mid] - iy;
      if (cmp === 0) return mid;
      if (cmp < 0) lo = mid + 1;
      else hi = mid - 1;
    }
    return -1;
  }

  /** Nearest vertex within toleranceM (default 250 m — the phone's snap
   * tolerance), or null. Searches the 3x3 cell neighborhood; ties resolve
   * to the lowest vertex id, matching Router.snap on the phone. */
  async nearest(at: LonLat, toleranceM: number = DEFAULT_TOLERANCE_M): Promise<SnapHit | null> {
    const [lon, lat] = at;
    const ix = Math.floor((lon + 180) / this.cellDeg);
    const iy = Math.floor((lat + 90) / this.cellDeg);

    const ranges: EntryRange[] = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const idx = this.findCell(ix + dx, iy + dy);
        if (idx < 0) continue;
        const count = this.cellCount[idx];
        if (count > 0) ranges.push({ start: this.cellFirst[idx], count });
      }
    }
    if (ranges.length === 0) return null;

    let bestId = -1;
    let bestAt: LonLat = [0, 0];
    let bestD = toleranceM;
    for (const range of mergeRanges(ranges)) {
      const buf = await this.src.read(this.entriesBase + range.start * ENTRY_BYTES, range.count * ENTRY_BYTES);
      const view = new DataView(buf);
      for (let i = 0; i < range.count; i++) {
        const off = i * ENTRY_BYTES;
        const id = view.getUint32(off, true);
        const vlon = view.getFloat32(off + 4, true);
        const vlat = view.getFloat32(off + 8, true);
        const d = haversineM(at, [vlon, vlat]);
        if (d < bestD || (d === bestD && id < bestId)) {
          bestD = d;
          bestId = id;
          bestAt = [vlon, vlat];
        }
      }
    }

    return bestId >= 0 ? { id: bestId, at: bestAt, distM: bestD } : null;
  }
}
